import { useCallback, useEffect, useState } from 'react';
import Evaporate from 'evaporate';
import crypto from 'crypto';
import { Buffer } from 'buffer';
import cookies from 'browser-cookies';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { toS3Url } from '@scale/shared/utils';
import logger from '@scale/frontend-lib/logging';

export interface MultipartUploadHook {
  initialized: boolean;
  upload: ({
    name,
    file,
    onProgress,
    onComplete,
    onError,
  }: {
    name: string;
    file: File;
    onProgress?: (progress: number) => void;
    onComplete?: (xhr: any, awsObjectKey: string) => void;
    onError?: (message: string) => void;
  }) => string;
}

const useMultipartUpload = (subtaskId?: string, userId?: string): MultipartUploadHook => {
  const [awsSessionToken, setAwsSessionToken] = useState<string>('');
  const [evaporate, setEvaporate] = useState<Evaporate | null>(null);
  const [bucket, setBucket] = useState<string>('');

  const generateCustomAuthMethod = useCallback(
    (
        awsSecretKey: string,
      ): ((
        _signParams: any,
        _signHeaders: any,
        stringToSign: string,
        _signatureDateTime: any,
        _canonicalRequest: any,
      ) => Promise<string>) =>
      (
        _signParams: any,
        _signHeaders: any,
        stringToSign: string,
        _signatureDateTime: any,
        _canonicalRequest: any,
      ): Promise<string> => {
        const stringToSignDecoded = decodeURIComponent(stringToSign);
        const requestScope = stringToSignDecoded.split('\n')[2];
        const [date, region] = requestScope.split('/');

        return new Promise(function (resolve) {
          const signature = (
            [`AWS4${awsSecretKey}`, date, region, 's3', 'aws4_request', stringToSignDecoded] as (
              | string
              | Buffer
            )[]
          ).reduce((prev, curr) => {
            const hmac = crypto.createHmac('sha256', prev).update(curr);
            return curr === stringToSignDecoded ? hmac.digest('hex') : hmac.digest();
          }) as string;
          resolve(signature);
        });
      },
    [],
  );

  const fetchAwsCreds = useCallback(() => {
    if (!subtaskId || !userId) {
      logger.warn('subtaskId and userId must be provded for useMultipartUpload');
      throw new Error('subtaskId and userId must be provded for useMultipartUpload');
    }

    return apiFetch<{
      awsAccessKey: string;
      awsSecretKey: string;
      awsSessionToken: string;
      awsRegion: string;
      awsBucket: string;
    }>(null, `internal/multipartupload/getAwsCreds`, {
      method: 'GET',
      query: {
        subtaskId,
      },
    });
  }, [subtaskId, userId]);

  const refreshAwsCreds = useCallback(async () => {
    try {
      const awsCreds = await fetchAwsCreds();
      setAwsSessionToken(awsCreds.awsSessionToken);
      const _e_ = await Evaporate.create({
        aws_key: awsCreds.awsAccessKey,
        bucket: awsCreds.awsBucket,
        awsRegion: awsCreds.awsRegion,
        partSize: 1024 * 1024 * 6, // 6MB
        logging: process.env.NODE_ENV === 'development',
        customAuthMethod: generateCustomAuthMethod(awsCreds.awsSecretKey),
        awsSignatureVersion: '4',
        computeContentMd5: true,
        cryptoMd5Method: data =>
          crypto.createHash('md5').update(Buffer.from(data)).digest('base64'),
        cryptoHexEncodedHash256: data =>
          crypto
            .createHash('sha256')
            // lol typescript
            .update(typeof data === 'string' ? Buffer.from(data) : Buffer.from(data!))
            .digest('hex'),
        signHeaders: {
          'X-CSRF-Token': cookies.get('_csrf') ?? 'blank',
        },
      });
      setEvaporate(_e_);
      setBucket(awsCreds.awsBucket);

      return _e_;
    } catch (e) {
      setEvaporate(null);
    }
  }, [generateCustomAuthMethod, fetchAwsCreds]);

  useEffect(() => {
    refreshAwsCreds();
  }, [subtaskId, refreshAwsCreds]);

  const upload = useCallback(
    ({
      name,
      file,
      onProgress,
      onComplete,
      onError,
    }: {
      name: string;
      file: File;
      onProgress?: (progress: number) => void;
      onComplete?: (_xhr: any, awsObjectKey: string) => void;
      onError?: (message: string) => void;
    }): string => {
      if (!name || !file) {
        throw Error('Name and file must be provided to upload a file');
      }
      if (!evaporate) {
        throw Error('Multipart Upload hook is not initialized');
      }

      const xAmzHeader = { 'x-amz-security-token': awsSessionToken };

      const s3FileKey = `${subtaskId}/dc_uploads/${userId}/${name}`;
      evaporate.add({
        name: s3FileKey,
        file,
        xAmzHeadersCommon: xAmzHeader,
        xAmzHeadersAtInitiate: xAmzHeader,
        progress: onProgress,
        complete: onComplete,
        error: onError,
      });

      const unsignedDataUrl = toS3Url(bucket, s3FileKey);
      return unsignedDataUrl;
    },
    [evaporate, bucket, awsSessionToken, subtaskId, userId],
  );

  return {
    initialized: !!evaporate,
    upload,
  };
};

export default useMultipartUpload;
